import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../auth/AuthProvider';
import { normalizeOrder } from '../lib/normalizeOrder';

function asDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function money(value) {
  const num = Number(value || 0);
  return `R ${num.toLocaleString('en-ZA', { maximumFractionDigits: 0 })}`;
}

function dateKey(value) {
  const dt = asDate(value);
  if (!dt) return '';
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function todayKey() {
  return dateKey(new Date());
}

function displayDate(value) {
  const date = asDate(value);
  if (!date) return 'No date';
  return date.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: '2-digit' });
}

function normalizeSearchValue(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function searchableText(...values) {
  return values
    .flatMap((value) => Array.isArray(value) ? value : [value])
    .map((value) => String(value || '').toLowerCase())
    .join(' ');
}

function timeLabel(value) {
  const dt = asDate(value);
  if (!dt) return '—';
  return dt.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function shortTime(value) {
  const dt = asDate(value);
  if (!dt) return '—';
  return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function minutesBetween(start, end) {
  const a = asDate(start);
  const b = asDate(end);
  if (!a || !b) return null;
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / 60000));
}

function durationLabel(minutes) {
  if (minutes == null || !Number.isFinite(Number(minutes))) return '—';
  const total = Math.max(0, Math.round(Number(minutes)));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim?.() !== '') || '';
}

function lineName(item = {}) {
  return String(item.name || item.productName || item.title || item.sku || 'Item').trim();
}

function lineQty(item = {}) {
  return Number(item.qty ?? item.quantity ?? item.count ?? 1) || 1;
}

function lineTotal(item = {}) {
  const direct = Number(item.lineTotal ?? item.total ?? item.amount ?? 0);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const unit = Number(item.price ?? item.unitPrice ?? item.sellPrice ?? item.value ?? 0) || 0;
  return unit * lineQty(item);
}

function lineKey(item = {}, index = 0) {
  return String(item.__lineKey || item.lineKey || item.cartLineId || item.lineId || item.orderLineId || item.rowId || item.uid || item.id || item.productId || `${index}_${lineName(item)}_${lineQty(item)}_${lineTotal(item)}`);
}

function isAddition(item = {}) {
  const id = String(item.id || item.lineId || item.productId || '').toLowerCase();
  const status = String(item.status || item.itemStatus || item.type || '').toLowerCase();
  return item.isSwap === true || item.isAddition === true || item.addedBySite || item.addedAtLabel || item.swapFor || id.startsWith('swap_') || status.includes('addition') || status.includes('swap');
}

function isUnavailable(item = {}, index = 0, order = {}) {
  const unavailableMap = order?.raw?.flags?.unavailableMap || order?.flags?.unavailableMap || {};
  const unavailableItems = order?.raw?.unavailableItems || [];
  const unavailableNames = order?.raw?.unavailableItemNames || [];
  const key = lineKey(item, index);
  const name = lineName(item);
  return Boolean(
    item.unavailable ||
    item.unavailableBySite ||
    item.removed ||
    item.outOfStock ||
    unavailableMap[key] ||
    unavailableMap[name] ||
    unavailableItems.includes(key) ||
    unavailableItems.includes(name) ||
    unavailableNames.includes(name)
  );
}

function getOriginalLines(order = {}) {
  const raw = order.raw || order;
  const source = Array.isArray(raw.originalOrderItems) && raw.originalOrderItems.length
    ? raw.originalOrderItems
    : Array.isArray(raw.items) ? raw.items : Array.isArray(order.items) ? order.items : [];
  return source.filter((item) => !isAddition(item));
}

function getAdditions(order = {}) {
  const raw = order.raw || order;
  const fromAdditions = Array.isArray(raw.additions) ? raw.additions : [];
  const fromItems = Array.isArray(raw.items) ? raw.items.filter(isAddition) : [];
  const seen = new Set();
  return [...fromAdditions, ...fromItems].filter((item, index) => {
    const key = lineKey(item, index);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function totalsFor(order = {}) {
  const raw = order.raw || order;
  const originalLines = getOriginalLines(order);
  const additions = getAdditions(order);
  const delivery = Number(raw?.pricing?.deliveryFee ?? raw.deliveryFee ?? order.deliveryFee ?? 0) || 0;
  const originalSubtotal = originalLines.reduce((sum, item) => sum + lineTotal(item), 0);
  const originalTotal = Number(raw.originalPaidTotal ?? raw.originalOrderTotal ?? raw?.pricing?.originalTotal ?? raw?.pricing?.paidTotal ?? raw?.pricing?.total ?? order.total ?? 0) || (originalSubtotal + delivery);
  const liveSubtotal = originalLines.reduce((sum, item, index) => sum + (isUnavailable(item, index, order) ? 0 : lineTotal(item)), 0) + additions.reduce((sum, item) => sum + lineTotal(item), 0);
  const totalToCollect = liveSubtotal + delivery;
  const difference = totalToCollect - originalTotal;
  return { originalTotal, liveSubtotal, delivery, totalToCollect, difference };
}

function archiveStatus(order = {}) {
  const raw = order.raw || order;
  const value = String(raw.archiveType || raw.archivedType || raw.archiveReasonType || raw.stage || order.stage || raw.status || '').toLowerCase();
  if (value.includes('reroute') || raw.reroutedToStore || raw.reroutedFromStore) return 'Rerouted';
  if (value.includes('cancel')) return 'Cancelled';
  if (value.includes('deliver') || value.includes('complete')) return 'Completed';
  return raw.archived ? 'Archived' : 'Completed';
}

function archiveDate(order = {}) {
  const raw = order.raw || order;
  return firstValue(raw.archivedAt, raw.timings?.archivedAt, raw.timings?.deliveredAt, raw.timings?.completedAt, raw.timings?.cancelledAt, raw.updatedAt, order.updatedAt, order.createdAt);
}

function actorName(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.name || value.displayName || value.email || value.userId || '';
}

function peopleFor(order = {}) {
  const raw = order.raw || order;
  return {
    online: firstValue(raw.assignedOnline, raw.assignment?.onlineName, raw.createdByName, raw.sentByName, raw.senderName, actorName(raw.createdBy), order.assignedOnline, order.sentByName, '—'),
    packer: firstValue(raw.storeProof?.packedBy, raw.storeProof?.collectedBy, raw.assignment?.storeUserName, raw.assignment?.packerName, raw.packerName, raw.packedBy, raw.assignedStoreUserName, raw.storeUserName, order.assignedStoreUser, '—'),
    driver: firstValue(raw.proof?.deliveredBy, raw.driverProof?.acceptedBy, raw.assignment?.driverName, raw.assignedDriverName, raw.assignedDriver, order.assignedDriver, '—'),
  };
}

function overallMinutes(order = {}) {
  const raw = order.raw || order;
  const start = firstValue(raw.timings?.createdAt, raw.placedAt, raw.createdAt, order.placedAt, order.createdAt);
  const end = firstValue(raw.timings?.deliveredAt, raw.timings?.completedAt, raw.timings?.cancelledAt, raw.timings?.archivedAt, raw.archivedAt, raw.updatedAt, order.updatedAt);
  return minutesBetween(start, end);
}

function proofCards(order = {}) {
  const raw = order.raw || order;
  const proof = raw.proof || {};
  const storeProof = raw.storeProof || {};
  const driverProof = raw.driverProof || {};
  return [
    { title: 'Packed', at: firstValue(storeProof.packedAt, raw.timings?.packedAt, raw.timings?.readyForDriverAt), url: firstValue(storeProof.packedPhotoUrl, proof.packedPhotoUrl) },
    { title: 'Handover', at: firstValue(storeProof.collectedAt, raw.timings?.collectedAt), url: firstValue(storeProof.collectedPhotoUrl, proof.collectedPhotoUrl) },
    { title: 'Collected', at: firstValue(driverProof.acceptedAt, raw.timings?.driverAcceptedAt, raw.timings?.outForDeliveryAt), url: firstValue(driverProof.acceptedPhotoUrl, driverProof.startedPhotoUrl, proof.driverAcceptedPhotoUrl, proof.driverStartedPhotoUrl) },
    { title: 'Delivered', at: firstValue(proof.deliveredAt, raw.timings?.deliveredAt), url: firstValue(proof.photoUrl, proof.deliveredPhotoUrl, proof.deliveryPhotoUrl) },
  ];
}

function eventDate(event = {}) {
  return firstValue(event.createdAt, event.at, event.clientAt);
}

function eventTitle(event = {}) {
  return firstValue(event.title, event.message, event.text, event.type, event.legacyType, 'Order event');
}

function historyRows(order = {}) {
  const raw = order.raw || order;
  const rows = Array.isArray(order.events) && order.events.length ? order.events : Array.isArray(raw.timeline) ? raw.timeline : [];
  return [...rows].sort((a, b) => (asDate(eventDate(b))?.getTime() || 0) - (asDate(eventDate(a))?.getTime() || 0));
}

function chatRows(order = {}) {
  const raw = order.raw || order;
  return Array.isArray(raw.linkChatMessages) ? [...raw.linkChatMessages].sort((a, b) => (asDate(b.createdAt)?.getTime() || 0) - (asDate(a.createdAt)?.getTime() || 0)) : [];
}

function ArchiveOrderCard({ order, open, onToggle, onPreviewProof }) {
  const raw = order.raw || order;
  const totals = totalsFor(order);
  const people = peopleFor(order);
  const status = archiveStatus(order);
  const overall = overallMinutes(order);
  const originalLines = getOriginalLines(order);
  const additions = getAdditions(order);
  const proofs = proofCards(order);
  const history = historyRows(order);
  const chats = chatRows(order);
  const reason = firstValue(raw.archiveReason, raw.rerouteReason, raw.cancelReason, raw.reason, raw.notes?.archive, 'No reason captured');

  return (
    <article data-archive-order-card={order.id} className="rounded-3xl border border-cyan-400/10 bg-[#07111d]/80 overflow-hidden">
      <button type="button" onClick={onToggle} className="w-full px-5 py-4 text-left hover:bg-white/[0.03]">
        <div className="grid gap-3 md:grid-cols-[140px_1fr_120px_120px] md:items-center">
          <div className="text-2xl font-black text-white">#{order.orderNumber || order.id}</div>
          <div>
            <div className="font-semibold text-white">{order.customerName}</div>
            <div className="text-sm text-slate-400">{order.store}</div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-200/75">{displayDate(archiveDate(order))}</div>
          </div>
          <div className="font-bold text-white md:text-right">{money(totals.totalToCollect)}</div>
          <div className="justify-self-start rounded-full bg-blue-500/20 px-3 py-1 text-xs font-bold text-blue-200 md:justify-self-end">{status}</div>
        </div>
      </button>

      {open ? (
        <div className="border-t border-white/10 p-5 space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <InfoTile label="Online" value={people.online} />
            <InfoTile label="Store packer" value={people.packer} />
            <InfoTile label="Driver" value={people.driver} />
            <InfoTile label="Overall time" value={durationLabel(overall)} />
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.25fr_0.9fr]">
            <Panel title="Customer">
              <h3 className="text-xl text-white">{order.customerName}</h3>
              <p className="mt-2 text-sm text-slate-300">{order.customerPhone || 'No phone captured'}</p>
              <p className="mt-2 text-sm text-slate-300">{order.address || raw.customer?.addressLine1 || 'No address captured'}</p>
              <p className="mt-3 text-sm text-white"><b>Archived by:</b> {firstValue(raw.archivedByName, raw.archivedBy, actorName(raw.archivedByActor), 'System')}</p>
              <p className="mt-2 text-sm text-white"><b>Date:</b> {dateKey(archiveDate(order)) || '—'}</p>
              <p className="mt-2 text-sm text-white"><b>Reason:</b> {reason}</p>
            </Panel>

            <Panel title="Full totals">
              <TotalRow label="Original paid/order total" value={money(totals.originalTotal)} />
              <TotalRow label="Live pack sub-total" value={money(totals.liveSubtotal)} />
              <TotalRow label="Delivery" value={money(totals.delivery)} />
              <TotalRow label="Total to collect" value={money(totals.totalToCollect)} strong />
              {totals.difference !== 0 ? <TotalRow label={totals.difference > 0 ? 'Customer owes us' : 'We owe customer'} value={money(Math.abs(totals.difference))} warn /> : null}
              <p className="pt-3 text-sm text-slate-300"><b>Payment:</b> {order.paymentMethod}</p>
            </Panel>
          </div>

          <div className="grid gap-4 xl:grid-cols-[1fr_0.95fr]">
            <Panel title="Actual order">
              <div className="space-y-2">
                {originalLines.map((item, index) => {
                  const unavailable = isUnavailable(item, index, order);
                  return (
                    <div key={lineKey(item, index)} className={`flex items-center justify-between gap-3 border-b border-white/10 py-2 ${unavailable ? 'text-slate-500 line-through decoration-rose-300/70' : 'text-white'}`}>
                      <span>{lineQty(item)} × {lineName(item)} {unavailable ? <span className="ml-2 rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] font-bold text-rose-200 no-underline">Unavailable</span> : null}</span>
                      <b>{money(lineTotal(item))}</b>
                    </div>
                  );
                })}
              </div>
              {additions.length ? (
                <div className="mt-4">
                  <div className="mb-2 text-xs uppercase tracking-[0.28em] text-cyan-300">Swaps / additions</div>
                  {additions.map((item, index) => (
                    <div key={lineKey(item, index)} className="flex items-center justify-between gap-3 border-b border-white/10 py-2 text-white">
                      <span>{lineQty(item)} × {lineName(item)} <span className="ml-2 text-xs text-cyan-300">Addition</span></span>
                      <b>{money(lineTotal(item))}</b>
                    </div>
                  ))}
                </div>
              ) : null}
            </Panel>

            <Panel title="Evidence">
              <div className="grid gap-3 sm:grid-cols-2">
                {proofs.map((proof) => (
                  <div key={proof.title} className="rounded-2xl bg-white/[0.03] p-3">
                    <div className="font-bold text-white">{proof.title}</div>
                    <div className="mt-1 text-xs text-slate-400">{timeLabel(proof.at)}</div>
                    {proof.url ? (
                      <button
                        type="button"
                        onClick={() => onPreviewProof?.(proof)}
                        className="mt-3 block w-full overflow-hidden rounded-xl border border-white/10 bg-black/20 text-left transition hover:border-cyan-300/60 hover:shadow-[0_0_18px_rgba(34,211,238,0.18)]"
                        title={`Preview ${proof.title} proof`}
                      >
                        <img src={proof.url} alt={`${proof.title} proof`} className="h-24 w-full object-cover" />
                      </button>
                    ) : <div className="mt-3 grid h-24 place-items-center rounded-xl bg-black/20 text-sm text-slate-500">No proof</div>}
                  </div>
                ))}
              </div>
            </Panel>
          </div>

          <div className="grid gap-4 xl:grid-cols-[1fr_0.95fr]">
            <Panel title="Order history">
              {history.length ? history.map((event, index) => (
                <div key={event.id || index} className="flex items-start justify-between gap-4 border-b border-white/10 py-2 text-sm">
                  <span className="text-white">{eventTitle(event)}</span>
                  <span className="whitespace-nowrap text-slate-400">{shortTime(eventDate(event))}</span>
                </div>
              )) : <p className="text-sm text-slate-500">No order history captured.</p>}
            </Panel>

            <Panel title="Last chats">
              {chats.length ? chats.slice(0, 6).map((chat) => (
                <div key={chat.id || chat.createdAt} className="mb-2 rounded-2xl bg-white/[0.04] p-3">
                  <div className="flex items-center justify-between gap-3 text-sm"><b className="text-white">{chat.actorName || 'Link user'}</b><span className="text-slate-500">{shortTime(chat.createdAt)}</span></div>
                  <div className="mt-2 text-sm text-slate-200">{chat.text || chat.attachmentName || 'Photo attachment'}</div>
                </div>
              )) : <p className="text-sm text-slate-500">No chat messages.</p>}
            </Panel>
          </div>
        </div>
      ) : null}
    </article>
  );
}

function archiveSearchBody(order = {}) {
  const raw = order.raw || {};
  return searchableText(
    order.id,
    order.orderNumber,
    order.customerName,
    order.customerPhone,
    order.address,
    order.store,
    order.paymentMethod,
    archiveStatus(order),
    displayDate(archiveDate(order)),
    raw.archivedByName,
    raw.archivedBy,
    raw.archiveReason,
    raw.cancelReason,
    raw.rerouteReason,
    getOriginalLines(order).map(lineName),
    getAdditions(order).map(lineName),
    chatRows(order).map((chat) => [chat.senderName, chat.author, chat.text, chat.message]),
    historyRows(order).map((event) => [eventTitle(event), displayDate(eventDate(event))])
  );
}

function makeArchiveSearchResults(query, archivedOrders = []) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return [];
  const normalizedQuery = normalizeSearchValue(q);
  const digitsQuery = q.replace(/\D+/g, '');

  return archivedOrders
    .map((order) => {
      const number = String(order.orderNumber || order.id || '');
      const numberDigits = number.replace(/\D+/g, '');
      const body = archiveSearchBody(order);
      const matched = body.includes(q) || normalizeSearchValue(body).includes(normalizedQuery) || (digitsQuery && numberDigits.includes(digitsQuery));
      if (!matched) return null;
      const totals = totalsFor(order);
      return {
        key: order.id,
        order,
        label: `#${number}`,
        detail: `${order.customerName || 'Customer'} · ${order.store || 'No site'} · ${displayDate(archiveDate(order))} · ${money(totals.totalToCollect)}`,
        meta: archiveStatus(order),
        score: digitsQuery && numberDigits.includes(digitsQuery) ? 300 : 120,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score)
    .slice(0, 12);
}

function ArchiveCommandSearchPanel({ query, results, onOpen }) {
  if (!query.trim()) return null;
  return (
    <div className="link-command-search-panel fixed left-1/2 top-[118px] z-[80] max-h-[calc(100vh-150px)] w-[min(560px,calc(100vw-32px))] -translate-x-1/2 rounded-3xl border border-cyan-300/25 bg-[#07111d]/95 p-4 shadow-[0_0_40px_rgba(34,211,238,0.16)] backdrop-blur-xl">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-slate-400">Command Search</div>
          <div className="mt-1 text-sm font-bold text-white">“{query}”</div>
        </div>
        <div className="rounded-full bg-cyan-400/15 px-3 py-1 text-xs font-bold text-cyan-100">{results.length} result{results.length === 1 ? '' : 's'}</div>
      </div>
      <div className="max-h-[calc(100vh-250px)] space-y-2 overflow-y-auto overscroll-contain pr-1">
        {results.length ? results.map((result) => (
          <button
            key={result.key}
            type="button"
            onClick={() => onOpen(result.order)}
            className="block w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-left hover:border-cyan-300/60 hover:bg-cyan-400/10"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="font-black text-white">{result.label}</div>
              <span className="rounded-full bg-blue-500/20 px-3 py-1 text-[11px] font-bold text-blue-100">{result.meta}</span>
            </div>
            <div className="mt-1 text-sm text-slate-300">{result.detail}</div>
          </button>
        )) : <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-slate-300">No archived orders found.</div>}
      </div>
    </div>
  );
}


function InfoTile({ label, value }) {
  return <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"><div className="text-xs uppercase tracking-[0.22em] text-slate-500">{label}</div><div className="mt-2 font-bold text-white">{value || '—'}</div></div>;
}

function Panel({ title, children }) {
  return <section className="rounded-2xl border border-white/10 bg-[#050d1b]/70 p-4"><div className="mb-3 text-xs uppercase tracking-[0.28em] text-cyan-300">{title}</div>{children}</section>;
}

function TotalRow({ label, value, strong, warn }) {
  return <div className={`flex items-center justify-between gap-3 border-b border-white/10 py-2 ${strong ? 'text-lg' : 'text-sm'}`}><span className="text-white">{label}</span><b className={warn ? 'text-amber-200' : 'text-white'}>{value}</b></div>;
}

export default function OnTimeArchivesPage() {
  const { claims } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const orgId = claims?.orgId || '';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [storeFilter, setStoreFilter] = useState('all');
  const [selectedDate, setSelectedDate] = useState(todayKey());
  const [openId, setOpenId] = useState('');
  const [previewProof, setPreviewProof] = useState(null);
  const [commandQuery, setCommandQuery] = useState('');

  useEffect(() => {
    if (!orgId) {
      setOrders([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError('');
    const unsub = onSnapshot(
      query(collection(db, 'orgs', orgId, 'ontimeOrders')),
      (snap) => {
        const rows = snap.docs.map((d) => normalizeOrder({ id: d.id, ...d.data() }));
        setOrders(rows);
        setLoading(false);
      },
      (err) => {
        setError(err?.message || 'Failed to load Link archives.');
        setLoading(false);
      }
    );
    return () => unsub();
  }, [orgId]);

  useEffect(() => {
    const syncCommandSearch = (event) => setCommandQuery(String(event?.detail?.query || ''));
    const clearCommandSearch = () => setCommandQuery('');
    window.addEventListener('link-command-search', syncCommandSearch);
    window.addEventListener('link-command-search-clear', clearCommandSearch);
    return () => {
      window.removeEventListener('link-command-search', syncCommandSearch);
      window.removeEventListener('link-command-search-clear', clearCommandSearch);
    };
  }, []);

  const archiveParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const selectedArchiveOrder = archiveParams.get('order') || '';
  const selectedArchiveId = archiveParams.get('id') || '';

  const allArchivedOrders = useMemo(() => {
    return orders.filter((order) => {
      const raw = order.raw || {};
      const status = archiveStatus(order).toLowerCase();
      return Boolean(raw.archived || raw.archiveType || raw.archivedAt || ['delivered', 'completed', 'cancelled', 'archived'].includes(String(order.stage || '').toLowerCase()) || status === 'rerouted');
    }).sort((a, b) => (asDate(archiveDate(b))?.getTime() || 0) - (asDate(archiveDate(a))?.getTime() || 0));
  }, [orders]);

  const archivedOrders = useMemo(() => {
    return allArchivedOrders.filter((order) => {
      const status = archiveStatus(order).toLowerCase();
      const number = String(order.orderNumber || order.id || '');
      const exactSelected = selectedArchiveId
        ? String(order.id || '') === String(selectedArchiveId)
        : selectedArchiveOrder
          ? number === String(selectedArchiveOrder) || number.replace(/^#/, '') === String(selectedArchiveOrder).replace(/^#/, '')
          : false;
      if (selectedArchiveOrder || selectedArchiveId) return exactSelected;
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (storeFilter !== 'all' && String(order.store || '').toLowerCase() !== String(storeFilter).toLowerCase()) return false;
      if (selectedDate && dateKey(archiveDate(order)) !== selectedDate) return false;
      return true;
    }).sort((a, b) => (asDate(archiveDate(b))?.getTime() || 0) - (asDate(archiveDate(a))?.getTime() || 0));
  }, [allArchivedOrders, selectedArchiveId, selectedArchiveOrder, selectedDate, statusFilter, storeFilter]);

  const stores = useMemo(() => Array.from(new Set(orders.map((order) => order.store).filter(Boolean))).sort((a, b) => a.localeCompare(b)), [orders]);

  const archiveSearchResults = useMemo(() => makeArchiveSearchResults(commandQuery, allArchivedOrders), [allArchivedOrders, commandQuery]);

  useEffect(() => {
    if ((selectedArchiveOrder || selectedArchiveId) && archivedOrders[0]) {
      setOpenId(archivedOrders[0].id);
      window.requestAnimationFrame(() => {
        document.querySelector(`[data-archive-order-card="${archivedOrders[0].id}"]`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [archivedOrders, selectedArchiveId, selectedArchiveOrder]);

  const openArchiveSearchResult = (order) => {
    if (!order) return;
    window.dispatchEvent(new CustomEvent('link-command-search-clear'));
    setCommandQuery('');
    navigate(`/ontime/archives?order=${encodeURIComponent(order.orderNumber || order.id || '')}&id=${encodeURIComponent(order.id || '')}`);
  };

  const showAllArchives = () => {
    setOpenId('');
    navigate('/ontime/archives');
  };

  return (
    <div className="link-archives-page space-y-6">
      <ArchiveCommandSearchPanel query={commandQuery} results={archiveSearchResults} onOpen={openArchiveSearchResult} />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.35em] text-cyan-300">Link archives</div>
          <h2 className="mt-2 text-4xl font-light text-white">Archived operational orders</h2>
        </div>
        <div className="flex flex-wrap gap-3">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rounded-2xl border border-white/10 bg-[#07111d] px-4 py-3 text-sm text-white">
            <option value="all">All</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="rerouted">Rerouted</option>
            <option value="archived">Archived</option>
          </select>
          <select value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)} className="rounded-2xl border border-white/10 bg-[#07111d] px-4 py-3 text-sm text-white">
            <option value="all">All Stores</option>
            {stores.map((store) => <option key={store} value={store}>{store}</option>)}
          </select>
          <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="rounded-2xl border border-white/10 bg-[#07111d] px-4 py-3 text-sm text-white" />
        </div>
      </div>

      {(selectedArchiveOrder || selectedArchiveId) ? (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-cyan-300/15 bg-cyan-400/5 px-4 py-3 text-sm text-white">
          <span>Showing the archived order selected from Command Search.</span>
          <button type="button" onClick={showAllArchives} className="rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2 text-xs font-bold text-white hover:border-cyan-300/50">Show all archives</button>
        </div>
      ) : null}

      {error ? <div className="rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{error}</div> : null}
      {loading ? <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-slate-400">Loading archived orders…</div> : null}

      <div className="space-y-4">
        {!loading && archivedOrders.length === 0 ? <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white">No archived orders found for the selected filters.</div> : null}
        {archivedOrders.map((order) => (
          <ArchiveOrderCard key={order.id} order={order} open={openId === order.id} onToggle={() => setOpenId((current) => current === order.id ? '' : order.id)} onPreviewProof={setPreviewProof} />
        ))}
      </div>


      {previewProof?.url ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 px-4 py-6 backdrop-blur-md" onClick={() => setPreviewProof(null)}>
          <div className="w-full max-w-3xl rounded-3xl border border-cyan-300/20 bg-[#07111d] p-5 shadow-[0_0_40px_rgba(34,211,238,0.16)]" onClick={(event) => event.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-cyan-300">{previewProof.title || 'Proof photo'}</div>
                <div className="mt-2 text-sm text-slate-300">{timeLabel(previewProof.at)}</div>
              </div>
              <button type="button" onClick={() => setPreviewProof(null)} className="rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-2 text-sm font-bold text-white hover:border-cyan-300/50">Close</button>
            </div>
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-black/30">
              <img src={previewProof.url} alt={`${previewProof.title || 'Proof'} preview`} className="block max-h-[72vh] w-full object-contain" />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
